import React, { useContext, useState } from 'react';
import styled from 'astroturf/react';
import { Body } from 'js/design_system/Body';
import { Spinner } from 'js/design_system/Spinner';
import { trackClick } from 'js/utils/trackClick';
import { CiterightContext } from './CiterightContextProvider';
import WorkCard from './components/WorkCard';
import { ClaimWithActions, WorkData } from './types';

// ─── Styled components ────────────────────────────────────────────────────────

const PreprintClaimSection = styled.div`
  margin-bottom: 32px;
`;

const ClaimBorderedCard = styled.div`
  border: 1px solid var(--stroke-light);
  border-radius: 8px;
  padding: 16px 16px 0;
  margin: 12px 0;
  background: var(--background-white, #ffffff);
`;

const ClaimItalicText = styled.p`
  font-style: italic;
  margin: 0 0 12px;
  color: var(--text-primary);
`;

const WorksContainer = styled.div`
  overflow: hidden;
  margin-top: 0;
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  color: var(--brand-colors-blue-600);
  cursor: pointer;
  font-size: 13px;
  padding: 8px 16px;
  width: 100%;
  text-align: left;
  border-top: 1px solid var(--stroke-light);

  &:hover {
    background: var(--brand-colors-neutral-200, #f8f8fb);
  }
`;

// ─── PreprintClaimCard ────────────────────────────────────────────────────────

interface PreprintClaimCardProps {
  claim: ClaimWithActions;
  works: WorkData[];
}

const PreprintClaimCard: React.FC<PreprintClaimCardProps> = ({
  claim,
  works,
}) => {
  const context = useContext(CiterightContext);
  const [expanded, setExpanded] = useState(false);
  const suggestedWorks = works;
  const topWork = suggestedWorks[0];
  const remainingWorks = suggestedWorks.slice(1);
  const remainingCount = remainingWorks.length;

  const CLAIM_TRUNCATE = 240;
  const isTruncated = claim.text.length > CLAIM_TRUNCATE;
  const truncatedClaim = isTruncated
    ? claim.text.slice(0, CLAIM_TRUNCATE).replace(/\s+\S*$/, '') + '\u2026'
    : claim.text;

  return (
    <PreprintClaimSection>
      <Body
        size="sm"
        style={{ color: 'var(--text-secondary)', marginBottom: '8px' }}
      >
        We found {suggestedWorks.length} reference
        {suggestedWorks.length !== 1 ? 's' : ''} to support
        {' \u201c'}
        {truncatedClaim}
        {'\u201d'}
      </Body>

      <ClaimBorderedCard>
        <ClaimItalicText>{claim.reason}</ClaimItalicText>

        <Body size="sm" bold style={{ marginBottom: '2px' }}>
          Top suggested reference
        </Body>

        {topWork && (
          <WorksContainer>
            <WorkCard
              workData={topWork}
              claimId={claim.id}
              claimText={claim.text}
              showAddToListButton
              showPaperEvaluationChatButton={false}
              pageContext="preprint_report"
            />
            {expanded &&
              remainingWorks.map((work, i) => (
                <WorkCard
                  key={i}
                  workData={work}
                  claimId={claim.id}
                  claimText={claim.text}
                  showAddToListButton
                  showPaperEvaluationChatButton={false}
                  pageContext="preprint_report"
                />
              ))}
            {remainingCount > 0 && (
              <ToggleButton
                onClick={() => {
                  trackClick({
                    source: expanded
                      ? 'citeright.preprint.claim.collapse'
                      : 'citeright.preprint.claim.expand',
                    entityId: context?.reportId ?? undefined,
                    extra: {
                      claim_id: claim.id,
                      remaining_count: remainingCount,
                    },
                  });
                  setExpanded(!expanded);
                }}
              >
                {expanded
                  ? 'Show fewer references \u25b4'
                  : `View ${remainingCount} more reference${
                      remainingCount !== 1 ? 's' : ''
                    } \u25be`}
              </ToggleButton>
            )}
          </WorksContainer>
        )}
      </ClaimBorderedCard>
    </PreprintClaimSection>
  );
};

// ─── Step 3 (reference suggestions) ──────────────────────────────────────────

export const PreprintSuggestionsStep: React.FC = () => {
  const context = useContext(CiterightContext);

  if (!context) return null;

  if (context.isPolling && !context.report) {
    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', padding: '64px 0' }}
      >
        <Spinner />
      </div>
    );
  }

  const refTitles = new Set(
    (context.report?.references ?? []).map((r) => r.work?.title?.toLowerCase()),
  );

  const isAlreadyCited = (title: string | undefined) =>
    !!title && refTitles.has(title.toLowerCase());

  const claimsWithSuggestions = (context.report?.claims ?? [])
    .map((c) => ({
      claim: c,
      works: (c.search?.suggested_works ?? []).filter(
        (w) => !isAlreadyCited(w.work?.title),
      ),
    }))
    .filter(({ works }) => works.length > 0)
    .slice(0, 3);

  const uniqueWorkCount = new Set(
    claimsWithSuggestions.flatMap(({ works }) =>
      works.map((w) => w.work?.id).filter(Boolean),
    ),
  ).size;

  return (
    <div>
      <Body size="md" style={{ marginBottom: '24px' }}>
        We found {uniqueWorkCount} paper{uniqueWorkCount !== 1 ? 's' : ''} you
        have not cited and may strengthen your manuscript.
      </Body>
      {claimsWithSuggestions.map(({ claim, works }) => (
        <PreprintClaimCard key={claim.id} claim={claim} works={works} />
      ))}
    </div>
  );
};

export default PreprintSuggestionsStep;
